import React, { memo } from "react";
import {
  Box,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import Add from "@mui/icons-material/Add";
import { highlightSnippet } from "./chatbotTreeModel";
import ChatbotSlotTooltipContent from "./ChatbotSlotTooltipContent";
import { slotStepName } from "../../utils/chatbotNavigationActions";
import { eventoFromSlot, EVENTO_INATIVIDADE } from "../../utils/chatbotSlotEvent";

function ChatbotTreeRow({
  row,
  isSelected,
  isSimulatorActive,
  isDirectMatch,
  searchQuery,
  onSelect,
  onToggle,
  onAddChild,
}) {
  const { slot, level, hasChildren, isOpen } = row;
  const isExcecao = slot.identificador === "Exceção";
  const isRoot = slot.id_pai === 0 || slot.id_pai === "0";
  const respostaSnippet = searchQuery ? highlightSnippet(slot.resposta, searchQuery) : null;
  const label = slotStepName(slot);
  const inactivityEvent = slot.global && eventoFromSlot(slot).tipo === EVENTO_INATIVIDADE;

  const rowButton = (
    <ListItemButton
      dense
      selected={isSelected}
      onClick={() => onSelect?.(slot)}
      sx={{
        pl: 1 + level * 2.4,
        pr: 1,
        minHeight: 44,
        borderLeft: level > 0 ? "1px solid" : "none",
        borderColor: "divider",
        borderRadius: 1,
        mb: 0.25,
        bgcolor: isDirectMatch && searchQuery ? "action.selected" : undefined,
        outline: isSimulatorActive ? "2px solid" : "none",
        outlineColor: isSimulatorActive ? "success.main" : "transparent",
        outlineOffset: -1,
        boxShadow: isSimulatorActive ? (theme) => `0 0 0 1px ${theme.palette.success.light}` : "none",
        "&.Mui-selected": {
          bgcolor: "primary.light",
          "&:hover": { bgcolor: "primary.light" },
        },
      }}
    >
      {hasChildren ? (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(row.id);
          }}
          sx={{ mr: 1 }}
        >
          {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      ) : (
        <Box sx={{ width: 32, mr: 0.5 }} />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
        {isExcecao ? (
          <WarningAmber fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
        ) : (
          <ChatBubbleOutline fontSize="small" color={isRoot ? "primary" : "action"} sx={{ flexShrink: 0 }} />
        )}
        <ListItemText
          primary={
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: isRoot ? 600 : 500,
                color: isExcecao ? "warning.dark" : "text.primary",
              }}
              title={label}
            >
              {label}
            </Typography>
          }
          secondaryTypographyProps={{ component: "div" }}
          secondary={
            respostaSnippet ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", fontStyle: "italic", mt: 0.25 }}
                noWrap
                title={slot.resposta}
              >
                {respostaSnippet}
              </Typography>
            ) : null
          }
          sx={{ mr: 1 }}
        />
        {slot.global ? (
          <Chip size="small" label="G" title="Global" sx={{ height: 18, fontSize: "0.65rem" }} />
        ) : null}
        {inactivityEvent ? (
          <Chip
            size="small"
            label="⏱"
            title={`Inatividade (${eventoFromSlot(slot).minutos_inatividade} min)`}
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
        ) : null}
        {isExcecao ? (
          <Chip size="small" label="!" color="warning" sx={{ height: 18, minWidth: 18 }} />
        ) : null}
        {isSimulatorActive ? (
          <Chip
            size="small"
            label="Simulador"
            color="success"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.62rem", flexShrink: 0 }}
          />
        ) : null}
      </Box>
      <Tooltip title="Adicionar subpasso">
        <span>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddChild?.(slot);
            }}
            disabled={isExcecao}
          >
            <Add fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </ListItemButton>
  );

  return (
    <Tooltip
      title={<ChatbotSlotTooltipContent slot={slot} />}
      placement="right"
      enterDelay={350}
      leaveDelay={80}
      arrow
      disableInteractive={false}
      slotProps={{
        tooltip: {
          sx: { maxWidth: 400, p: 1.25 },
        },
      }}
    >
      {rowButton}
    </Tooltip>
  );
}

export default memo(ChatbotTreeRow);
